import { readVehicle, writeVehicle, fitmentUrl, VEHICLE_FILTER_PARAMS } from '@theme/tt-vehicle-finder';

/**
 * The "your vehicle" bar under the header.
 *
 * Rendered client-side on purpose: the chosen truck lives in localStorage,
 * and baking it into the HTML would let Shopify's full-page cache serve one
 * shopper's vehicle to the next.
 */
class VehicleBar extends HTMLElement {
  connectedCallback() {
    this.#render();
    window.addEventListener('storage', this.#render);
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this.#render);
  }

  #render = () => {
    const vehicle = readVehicle();

    if (!vehicle) {
      this.replaceChildren();
      return;
    }

    const label = [vehicle.make, vehicle.model, vehicle.year || null].filter(Boolean).join(' ');
    const target = this.dataset.collectionUrl || '/collections/lift-kit';

    const bar = document.createElement('div');
    bar.className = 'tt-vbar';
    bar.innerHTML = `
      <div class="tt-wrap tt-vbar__r">
        <span class="tt-lbl">${this.dataset.label || 'Your vehicle'}</span>
        <b></b>
        <span class="tt-vbar__act">
          <a class="tt-linkish" href="${fitmentUrl(target, vehicle)}">${
            this.dataset.seeLabel || 'See what fits'
          }</a>
          <button class="tt-linkish" type="button" data-clear>${
            this.dataset.clearLabel || 'Clear'
          } &times;</button>
        </span>
      </div>`;

    // textContent, not innerHTML — the make and model come from storage a
    // previous page wrote, and storage is not a trusted source.
    bar.querySelector('b').textContent = label;
    bar.querySelector('[data-clear]').addEventListener('click', () => {
      writeVehicle(null);

      // Any finder on this page still shows the cleared vehicle in its
      // dropdowns — reset them too, so "clear" means what it says.
      document.querySelectorAll('tt-vehicle-finder').forEach((finder) => finder.reset?.());

      // Clearing the vehicle must also clear its filters from the page the
      // shopper is looking at — otherwise the bar disappears while the grid
      // silently stays narrowed to the truck they just removed.
      const url = new URL(window.location.href);
      const params = Object.values(VEHICLE_FILTER_PARAMS);
      if (params.some((p) => url.searchParams.has(p))) {
        for (const p of params) url.searchParams.delete(p);
        window.location.href = url.pathname + url.search;
        return;
      }

      this.#render();
    });

    this.replaceChildren(bar);
  };
}

if (!customElements.get('tt-vehicle-bar')) {
  customElements.define('tt-vehicle-bar', VehicleBar);
}
